/**
 * adminAccount.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import moment from "moment-timezone";

import { Account, BillingType, MemberRole, Membership, MemberType, Team, User } from "../models/auth";
import { InviteRequest, InviteResponse } from "../models/requests";

import { http } from "./http.service";

export interface AdminAccountService {
  getAccounts(search?: string, limit?: number): Promise<Array<Account>>,
  getAccount(accountID: string): Promise<Account>,
  saveAccount(account: Partial<Account>): Promise<Account>,
  updateAccount(account: Partial<Account>): Promise<Account>,
  sendInvite(req: InviteRequest): Promise<InviteResponse>,
  getBillingTypeForRole(role: MemberRole): BillingType,
  getAccountBills(): Promise<Array<AccountBill>>,
  getAccountMembers(accountID: string, search: string, limit: number): Promise<Array<Membership>>,
  updateMembership(accountID: string, userID: string, update: Partial<Membership>): Promise<Membership>,
  deleteMembership(accountID: string, userID: string): Promise<void>
  createTeam(accountID: string, team: Partial<Team>): Promise<void>,
  updateTeam(accountID: string, team: Partial<Team>): Promise<void>,
  getTeams(accountID: string, query?: string, limit?: number): Promise<Team[]>,
  deleteTeam(accountID: string, teamID: string): Promise<void>
}

interface AccountQueryOptions {
  q?: string,
  limit?: string,
  accountID?: string
}

interface MemberDeleteOptions {
  accountID: string,
  userID: string
}

export interface AccountBill {
  accountID: string,
  accountname: string
  paid: number,
  guest: number,
  free: number,
  claimedSlots: number,
}


class AccountServiceImpl implements AdminAccountService {
  async getAccounts(search?: string, limit?: number): Promise<Array<Account>> {
    const q: AccountQueryOptions = {};
    if (limit) { q.limit = limit.toString(); }
    if (search) { q.q = search; }
    const accounts = await http.get<Array<Account>, AccountQueryOptions>("/account/", q);
    for (const account of accounts) {
      if (account.created) {
        account.created = moment(account.created, moment.ISO_8601);
      }
    }
    return accounts;
  }

  async getAccount(accountID: string): Promise<Account> {
    const accountUri = encodeURIComponent(accountID);
    const account = await http.get<Account>(`/account/${accountUri}`);
    if (account.created) {
      account.created = moment(account.created, moment.ISO_8601);
    }
    return account;
  }

  saveAccount(account: Partial<Account>): Promise<Account> {
    return http.post<Partial<Account>, Account>("/account", account);
  }

  updateAccount(account: Partial<Account>): Promise<Account> {
    return http.put<Partial<Account>, Account>("/account", account);
  }

  sendInvite(req: InviteRequest): Promise<InviteResponse> {
    if (!req.accountID) { return bluebird.reject(new Error("accountID required")); }
    req.client_id = secrets.SC_CLIENT_ID;
    return http.post<InviteRequest, InviteResponse>("/account/invite", req);
  }

  // should be identical to server models/auth billingTypeForRole
  getBillingTypeForRole(role: MemberRole, type?: MemberType): BillingType {
    if (type && type === MemberType.temp) {
      return BillingType.guest;
    }
    return role === MemberRole.guest ? BillingType.guest : BillingType.paid;
  }

  async getAccountBills(): Promise<Array<AccountBill>> {
    return await http.get<Array<AccountBill>>("/account/billing");
  }

  async getAccountMembers(accountID: string, search: string, limit: number): Promise<Array<Membership>> {
    const q: AccountQueryOptions = { accountID: accountID };
    if (limit) {
      q.limit = limit.toString();
    }
    if (search) {
      q.q = search;
    }
    const members = await http.get<Array<Membership>, AccountQueryOptions>(
      "/account/member", q);
    for (const mem of members) {
      mem.avatarText = this.makeAvatarText(mem);
      mem.created = moment(mem.created, moment.ISO_8601);
    }
    return members;
  }

  async updateMembership(accountID: string, userID: string, update: Partial<Membership>): Promise<Membership> {
    update.accountID = accountID;
    update.userID = userID;
    const mem = await http.post<Partial<Membership>, Membership>("/account/member", update);
    mem.avatarText = this.makeAvatarText(mem);
    mem.created = moment(mem.created, moment.ISO_8601);
    return mem;
  }


  async deleteMembership(accountID: string, userID: string): Promise<void> {
    return http.delete<void, MemberDeleteOptions>("/account/member", { accountID, userID });
  }

  makeAvatarText(acctMember: Membership | User): string {
    if (acctMember.firstname && acctMember.lastname) {
      return acctMember.firstname.charAt(0) + acctMember.lastname.charAt(0);
    } else if (acctMember.lastname) {
      return acctMember.lastname.charAt(0) + acctMember.lastname.charAt(1);
    } else if (acctMember.firstname) {
      return acctMember.firstname.charAt(0) + acctMember.firstname.charAt(1);
    } else {
      return "XX";
    }
  }

  async createTeam(accountID: string, team: Team): Promise<void> {
    return http.post(`/account/${encodeURIComponent(accountID)}/team`, team);
  }

  async updateTeam(accountID: string, team: Team): Promise<void> {
    return http.put(`/account/${encodeURIComponent(accountID)}/team/${team._id}`, team);
  }
  async getTeams(accountID: string, search?: string, limit?: number): Promise<Team[]> {
    const query: AccountQueryOptions = {};
    if (limit) {
      query.limit = limit.toString()
    }
    if (search) {
      query.q = encodeURIComponent(search);
    }
    return http.get<Team[], AccountQueryOptions>(`/account/${encodeURIComponent(accountID)}/team`, query);
  }

  async deleteTeam(accountID: string, teamID: string): Promise<void> {
    return http.delete(`/account/${encodeURIComponent(accountID)}/team/${teamID}`);
  }
}

let accountService: AdminAccountService;

export function adminAccountServiceFactory(): AdminAccountService {
  if (!accountService) { accountService = new AccountServiceImpl(); }
  return accountService;
}